import * as React from 'react';
import {Form, Icon, Tooltip} from 'antd';
import {FormComponentProps} from 'antd/lib/form';

import locales from '../locales';
import {ErrorAlert} from '../common/ErrorAlert';
import DatePickerFormItem from '../common/form-items/DatePickerFormItem';
import * as formUtils from '../utils/form-utils';
import {
  ClientSummary,
  CreateInventorySaleEntry,
  ErrorResponse,
  InventorySale,
  InventorySaleEntry,
  SaveInventorySaleRequest,
} from '../api/dtos/dtos';
import SubmitCancelFormItem from '../common/form-items/SubmitCancelFormItem';
import SectionHeaderFormItem from '../common/form-items/SectionHeaderFormItem';
import InventorySaleEntryEditor from './SalesEntryEditor';
import HiddenFormItem from '../common/form-items/HiddenFormItem';
import ClientSelector from '../client-details/ClientSelector';
import {groupNumber} from "../utils/number-utils";
import * as dateUtils from "../utils/date-utils";
import moment from "moment/moment";
import TextAreaFormItem from '../common/form-items/TextAreaFormItem';
import SelectFormItem from '../common/form-items/SelectFormItem';
import NumberFormItem from '../common/form-items/NumberFormItem';
import {DeliveryMethod, getDeliveryMethodOptions} from "../common/DeliveryMethod";
import furoApi from '../api/FuroApi';
import {InvoiceType} from "../invoicing/invoice";

interface Props extends FormComponentProps {
  sale?: InventorySale;
  editable?: boolean;
  onFormSubmit: (request: SaveInventorySaleRequest) => void;
  onFormCancel: () => void;
  onSaleReload?: () => void;
}

interface State {
  selectedClient?: ClientSummary;
  error?: ErrorResponse;
}

const FIELD_CLIENT_ID = 'clientId';
const FIELD_CREATED_AT = 'createdAt';
const FIELD_COMMENT = 'comment';
const FIELD_INVOICE_COMMENT = 'invoiceComment';
const FIELD_DELIVERY_METHOD = 'deliveryMethod';
const FIELD_TRANSPORT_COST = 'transportCost';

class SaleForm extends React.Component<Props, State> {

  constructor(props: Props) {
    super(props);
    this.state = {
      selectedClient: props.sale?.client,
      error: undefined,
    }
  }

  public componentDidUpdate(prevProps: Readonly<Props>) {
    if (prevProps.sale !== this.props.sale) {
      this.setState({
        selectedClient: this.props.sale?.client,
        error: undefined,
      });
    }
  }

  public render() {
    const {form, sale, editable = true} = this.props;

    const createMode = sale === undefined;

    return (
      <>
        <Form>
          <HiddenFormItem
            form={form}
            fieldId={FIELD_CLIENT_ID}
            label={locales.client}
            required={true}
            initialValue={this.state.selectedClient?.id}
          >
            <ClientSelector
              client={this.state.selectedClient}
              editable={editable}
              onSelect={this.onClientSelect}
            />
          </HiddenFormItem>

          <DatePickerFormItem
            form={form}
            fieldId={FIELD_CREATED_AT}
            label={locales.created_at}
            initialValue={sale?.createdAt ? dateUtils.toMomentOrThrow(sale.createdAt) : moment()}
            required={true}
            showTime={true}
            disabled={!editable}
          />

          <TextAreaFormItem
            form={form}
            fieldId={FIELD_COMMENT}
            initialValue={sale?.comment}
            label={locales.comment}
            required={false}
            disabled={!editable}
            maxLength={255}
          />

          <TextAreaFormItem
            form={form}
            fieldId={FIELD_INVOICE_COMMENT}
            initialValue={sale?.invoiceComment}
            label={
              <span>
                {locales.invoice_comment}{' '}
                <Tooltip title={locales.sale_invoice_comment_tooltip}>
                  <Icon type="question-circle" aria-label={locales.sale_invoice_comment_tooltip}/>
                </Tooltip>
              </span>
            }
            required={false}
            disabled={!editable}
            maxLength={255}
          />


          <SelectFormItem
            form={form}
            fieldId={FIELD_DELIVERY_METHOD}
            label={locales.delivery_method}
            initialValue={sale?.deliveryMethod ?? null}
            required={true}
            disabled={!editable}
            options={getDeliveryMethodOptions()}
          />

          {form.getFieldValue(FIELD_DELIVERY_METHOD) === DeliveryMethod.COURIER &&
              <NumberFormItem
                  form={form}
                  fieldId={FIELD_TRANSPORT_COST}
                  initialValue={sale?.transportCost}
                  label={locales.transport_cost_huf}
                  required={true}
                  disabled={!editable}
                  min={0}
              >
                  <> {locales.app_currency}</>
              </NumberFormItem>
          }

          <SubmitCancelFormItem onSubmit={this.onSubmitClick} onCancel={this.onCancelClick} disabled={!editable}/>

          <SectionHeaderFormItem title={locales.entries}/>

          <InventorySaleEntryEditor
            entries={sale?.entries ?? []}
            disabled={createMode || !!sale?.deletedAt || (!!sale?.invoice && sale.invoice.type !== InvoiceType.STORNO)}
            onEntryDelete={this.onEntryDelete}
            onEntryUpdate={this.onEntryUpdate}
            onEntriesToAdd={this.onEntriesToAdd}
          />

          <Form.Item {...formUtils.formItemLayout} label={locales.total}>
            <b>{this.calculateTotalCost()} {locales.app_currency}</b>
          </Form.Item>

        </Form>

        {this.state.error && <ErrorAlert style={{marginTop: 16}} error={this.state.error}/>}
      </>
    );
  }

  private calculateTotalCost = (): string => {
    const entriesTotal = (this.props.sale?.entries ?? []).reduce((sum, entry) => sum + Number((entry.cost * entry.amount * (1 - (entry.discount ?? 0) / 100)).toFixed()), 0);
    const deliveryMethod = this.props.form.getFieldValue(FIELD_DELIVERY_METHOD);
    const transportCost = this.props.form.getFieldValue(FIELD_TRANSPORT_COST);
    const total = Number(entriesTotal.toFixed()) + (deliveryMethod === DeliveryMethod.COURIER ? (transportCost ?? 0) : 0);
    return groupNumber(total) ?? ''
  };

  private onClientSelect = (client?: ClientSummary) => {
    this.setState({selectedClient: client}, () => {this.props.form.setFieldsValue({[FIELD_CLIENT_ID]: client?.id})});
  };

  private onEntriesToAdd = (entriesToAdd: InventorySaleEntry[]) => {
    const saleId = this.props.sale?.id;
    if (saleId != null) {
      const entriesToCreate: CreateInventorySaleEntry[] = entriesToAdd
        .map(entry => entry.inventoryStockId)
        .filter((id): id is number => id != null)
        .map((inventoryStockItemId) => ({inventoryStockItemId}));

      if (entriesToCreate.length === 0) {
        return;
      }

      furoApi.sales.addEntries(saleId, entriesToCreate)
        .then(this.props.onSaleReload)
        .catch(this.onApiError);
      return;
    }
  };

  private onEntryDelete = (entryToDelete: InventorySaleEntry) => {
    const saleId = this.props.sale?.id;
    if (saleId != null && entryToDelete.id != null) {
      furoApi.sales.deleteEntry(saleId, entryToDelete.id)
        .then(this.props.onSaleReload)
        .catch(this.onApiError);
      return;
    }

  };

  private onEntryUpdate = (updatedEntry: InventorySaleEntry) => {
    const saleId = this.props.sale?.id;
    if (saleId != null && updatedEntry.id != null) {
      furoApi.sales.saveEntry(saleId, updatedEntry.id, updatedEntry)
        .then(this.props.onSaleReload)
        .catch(this.onApiError);
      return;
    }
  };

  private onSubmitClick = () => {
    this.setState({error: undefined});

    this.props.form.validateFieldsAndScroll((err, values: { [key: string]: any }) => {
      if (err) {
        return;
      }

      const deliveryMethod = values[FIELD_DELIVERY_METHOD];

      const request: SaveInventorySaleRequest = {
        clientId: this.state.selectedClient?.id!,
        createdAt: values[FIELD_CREATED_AT],
        comment: values[FIELD_COMMENT],
        invoiceComment: values[FIELD_INVOICE_COMMENT],
        deliveryMethod,
        transportCost: deliveryMethod === DeliveryMethod.COURIER ? values[FIELD_TRANSPORT_COST] : null,
      };

      this.props.onFormSubmit(request);
    });
  };

  private onApiError = (error: ErrorResponse) => {
    this.setState({error});
  };


  private onCancelClick = () => {
    this.props.form.resetFields();
    this.props.onFormCancel();
  };
}

export default Form.create<Props>()(SaleForm);



// WEBPACK FOOTER //
// ./src/sales/SaleForm.tsx